import { CREDIT_TYPE_OPTIONS, INSTALLMENT_OPTIONS } from "./datafastCardValidation";

export const ensureDatafastWidgetSelectors = (
	selectedInstallments: number,
	selectedCreditType: string,
	onInstallmentsChange?: (value: number, select: HTMLSelectElement) => void,
	onCreditTypeChange?: (value: string, select: HTMLSelectElement) => void,
	retry = 0
): void => {
	if (typeof document === "undefined") {
		return;
	}

	const forms = Array.from(
		document.querySelectorAll<HTMLFormElement>("form.wpwl-form-card, form.wpwl-form-registrations")
	);

	if (forms.length === 0) {
		if (retry < 10) {
			setTimeout(() => {
				ensureDatafastWidgetSelectors(
					selectedInstallments,
					selectedCreditType,
					onInstallmentsChange,
					onCreditTypeChange,
					retry + 1
				);
			}, 100);
		}
		return;
	}

	forms.forEach((form) => {
		const button = form.querySelector(".wpwl-button");
		if (!button || !button.parentElement) {
			return;
		}

		let installmentsGroup = form.querySelector<HTMLElement>(".bcommerce-installments-group");
		if (!installmentsGroup) {
			installmentsGroup = document.createElement("div");
			installmentsGroup.className = "wpwl-group bcommerce-installments-group";
			installmentsGroup.style.marginBottom = "8px";

			const label = document.createElement("label");
			label.className = "wpwl-label";
			label.textContent = "Diferidos (meses)";

			const wrapper = document.createElement("div");
			wrapper.className = "wpwl-wrapper";

			const select = document.createElement("select");
			select.className = "wpwl-control";
			select.dataset.bcommerceWidget = "installments";
			wrapper.appendChild(select);

			installmentsGroup.appendChild(label);
			installmentsGroup.appendChild(wrapper);
			button.parentElement.insertBefore(installmentsGroup, button);
		}

		const installmentsSelect = installmentsGroup.querySelector<HTMLSelectElement>("select[data-bcommerce-widget=\"installments\"]");
		if (!installmentsSelect) {
			return;
		}

		installmentsSelect.name = "recurring.numberOfInstallments";
		installmentsSelect.dataset.bcommerceManaged = "true";

		installmentsSelect.innerHTML = "";
		INSTALLMENT_OPTIONS.forEach((option) => {
			const opt = document.createElement("option");
			opt.value = String(option);
			opt.textContent = option === 0 ? "0 (corriente)" : `${option}`;
			installmentsSelect.appendChild(opt);
		});

		const normalizedInstallments = String(Number.isFinite(selectedInstallments) ? selectedInstallments : 0);
		installmentsSelect.value = normalizedInstallments;

		if (!installmentsSelect.dataset.bcommerceBound) {
			installmentsSelect.addEventListener("change", (event) => {
				const target = event.target as HTMLSelectElement;
				const value = Number.parseInt(target.value, 10);
				const normalized = Number.isFinite(value) ? value : 0;
				target.dataset.bcommerceManaged = "true";
				onInstallmentsChange?.(normalized, target);
			});
			installmentsSelect.dataset.bcommerceBound = "true";
		}

		let creditTypeGroup = form.querySelector<HTMLElement>(".bcommerce-credit-type-group");
		if (!creditTypeGroup) {
			creditTypeGroup = document.createElement("div");
			creditTypeGroup.className = "wpwl-group bcommerce-credit-type-group";
			creditTypeGroup.style.marginBottom = "8px";

			const label = document.createElement("label");
			label.className = "wpwl-label";
			label.textContent = "Tipo de crédito";

			const wrapper = document.createElement("div");
			wrapper.className = "wpwl-wrapper";

			const select = document.createElement("select");
			select.className = "wpwl-control";
			select.dataset.bcommerceWidget = "credit-type";
			wrapper.appendChild(select);

			creditTypeGroup.appendChild(label);
			creditTypeGroup.appendChild(wrapper);
			button.parentElement.insertBefore(creditTypeGroup, button);
		}

		const creditTypeSelect = creditTypeGroup.querySelector<HTMLSelectElement>("select[data-bcommerce-widget=\"credit-type\"]");
		if (!creditTypeSelect) {
			return;
		}

		creditTypeSelect.name = "customParameters[SHOPPER_TIPOCREDITO]";
		creditTypeSelect.dataset.bcommerceManaged = "true";

		creditTypeSelect.innerHTML = "";
		CREDIT_TYPE_OPTIONS.forEach(({ value, label }) => {
			const opt = document.createElement("option");
			opt.value = value;
			opt.textContent = label;
			creditTypeSelect.appendChild(opt);
		});

		const normalizedCreditType = (selectedCreditType || "00").padStart(2, "0");
		creditTypeSelect.value = normalizedCreditType;

		if (!creditTypeSelect.dataset.bcommerceBound) {
			creditTypeSelect.addEventListener("change", (event) => {
				const target = event.target as HTMLSelectElement;
				const value = (target.value || "00").padStart(2, "0");
				target.dataset.bcommerceManaged = "true";
				onCreditTypeChange?.(value, target);
			});
			creditTypeSelect.dataset.bcommerceBound = "true";
		}
	});
};

export const ensureDatafastSaveCardOption = (
	defaultChecked: boolean,
	onToggle?: (value: boolean, checkbox: HTMLInputElement, form?: HTMLFormElement) => void,
	retry = 0
): void => {
	if (typeof document === "undefined") {
		return;
	}

	const forms = Array.from(
		document.querySelectorAll<HTMLFormElement>("form.wpwl-form-card, form.wpwl-form-registrations")
	);

	if (forms.length === 0) {
		if (retry < 10) {
			setTimeout(() => {
				ensureDatafastSaveCardOption(defaultChecked, onToggle, retry + 1);
			}, 100);
		}
		return;
	}

	forms.forEach((form) => {
		const button = form.querySelector(".wpwl-button");
		if (!button || !button.parentElement) {
			return;
		}

		let group = form.querySelector<HTMLElement>(".bcommerce-save-card-group");
		if (!group) {
			group = document.createElement("div");
			group.className = "wpwl-group bcommerce-save-card-group";
			group.style.marginBottom = "8px";

			const label = document.createElement("label");
			label.className = "wpwl-label";
			label.style.display = "flex";
			label.style.alignItems = "center";
			label.style.gap = "8px";

			const checkbox = document.createElement("input");
			checkbox.type = "checkbox";
			checkbox.dataset.bcommerceWidget = "save-card";
			checkbox.className = "bcommerce-save-card-checkbox";
			checkbox.style.width = "16px";
			checkbox.style.height = "16px";
			checkbox.style.cursor = "pointer";

			const text = document.createElement("span");
			text.textContent = "Guardar tarjeta para futuras compras";

			label.appendChild(checkbox);
			label.appendChild(text);

			const helper = document.createElement("div");
			helper.className = "wpwl-hint";
			helper.textContent = "Solo se pedirá el CVV en siguientes pagos si guardas la tarjeta.";

			group.appendChild(label);
			group.appendChild(helper);
			button.parentElement.insertBefore(group, button);
		}

		const checkbox = group.querySelector<HTMLInputElement>("input[type=\"checkbox\"][data-bcommerce-widget=\"save-card\"]");
		if (!checkbox) {
			return;
		}

		checkbox.name = "createRegistration";
		checkbox.dataset.bcommerceManaged = "true";
		checkbox.checked = defaultChecked;

		if (!checkbox.dataset.bcommerceBound) {
			checkbox.addEventListener("change", (event) => {
				const target = event.target as HTMLInputElement;
				const checked = target.checked;
				onToggle?.(checked, target, form);
			});
			checkbox.dataset.bcommerceBound = "true";
		}
	});
};
